<script setup>
// ============================================================================
// BonBoZ: TRANG CRM — tra cứu.
//
// BA TAB, mặc định Khách hàng:
//   khach  một row = một contact
//   ad     một row = một quảng cáo, gộp mọi lần khách bấm
//   inbox  một row = một tin nhắn
//
// Cột có `khoaLink` là key trỏ sang bản ghi khác. Bấm vào MỞ PANEL bên phải chứ
// không đổi tab: đang dò một danh sách mà đổi tab là mất chỗ đang đọc.
//
// KHÔNG dựng lại hồ sơ 360 — đã có trong khung chat (EpointCrmPanel.vue của đội
// CRM). Panel chỉ dẫn sang đó.
//
// Bảng tự dựng chứ không dùng BaseTable: cần cột bật/tắt được và panel.
// ============================================================================
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { khachApi, adApi, inboxApi } from 'dashboard/api/bonboz/adTouchpoints';
import Button from 'dashboard/components-next/button/Button.vue';
import { TAB, COT_THEO_TAB, COT_CSV_THEO_TAB, dungCsv } from './cot';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');

const tab = ref('khach');
const dangTai = ref(false);
const loi = ref('');
const hangs = ref([]);
const tong = ref(0);
const boLoc = ref({ from: '', to: '', inbox_id: '' });
const chiTuQuangCao = ref(true);
const hienBangChonCot = ref(false);
const panel = ref(null);
const dangTaiPanel = ref(false);

const nhan = khoa => t(`BONBOZ.CRM_TRANG.${khoa}`);

const API = { khach: khachApi, ad: adApi, inbox: inboxApi };
const TRUONG_PANEL_AD = [
  'ad_id',
  'campaign_name',
  'adset_name',
  'ad_account_name',
  'effective_status',
  'contact_count',
  'touch_count',
];

// Mỗi tab nhớ riêng cột đang bật: cột tab Ad không liên quan gì tab Inbox.
const cotBat = ref(
  Object.fromEntries(
    TAB.map(ten => [
      ten,
      new Set(COT_THEO_TAB[ten].filter(c => c.mac_dinh).map(c => c.khoa)),
    ])
  )
);

const cotTab = computed(() => COT_THEO_TAB[tab.value]);
const cotHien = computed(() =>
  cotTab.value.filter(c => cotBat.value[tab.value].has(c.khoa))
);

const thamSo = () => {
  const p = { limit: 100 };
  if (tab.value === 'inbox') {
    p.per_page = 100;
    if (chiTuQuangCao.value) p.only_ads = 'true';
  }
  if (tab.value === 'khach') {
    if (!chiTuQuangCao.value) p.only_ads = 'false';
    if (boLoc.value.from) p.from = boLoc.value.from;
    if (boLoc.value.to) p.to = boLoc.value.to;
    if (boLoc.value.inbox_id) p.inbox_id = boLoc.value.inbox_id;
  }
  return p;
};

const nap = async () => {
  dangTai.value = true;
  loi.value = '';
  try {
    const { data } = await API[tab.value].list(thamSo());
    hangs.value = data.payload || [];
    tong.value = data.meta?.total ?? hangs.value.length;
  } catch (e) {
    loi.value = `${nhan('LOI')}: ${e?.response?.status || e.message}`;
    hangs.value = [];
    tong.value = 0;
  } finally {
    dangTai.value = false;
  }
};

const doiTab = ten => {
  tab.value = ten;
  panel.value = null;
  hangs.value = [];
  nap();
};

// Panel chỉ gọi API cho ad. Hội thoại thì dữ liệu đã nằm trong row rồi, gọi
// thêm một vòng chỉ để lấy thứ đang cầm trên tay là thừa.
const moPanel = async (cot, hang) => {
  const giatri = cot.lay(hang);
  if (!giatri) return;
  if (cot.khoaLink === 'inbox') {
    // Khong goi API: moi thong tin hoi thoai da nam san trong row.
    panel.value = { loai: 'inbox', conversation_id: giatri, hang };
    return;
  }
  panel.value = { loai: 'ad', ad_id: giatri, du_lieu: null };
  dangTaiPanel.value = true;
  try {
    const { data } = await adApi.show(giatri);
    panel.value = { loai: 'ad', ad_id: giatri, du_lieu: data.payload };
  } catch (e) {
    panel.value = {
      loai: 'ad',
      ad_id: giatri,
      loi: e?.response?.status || e.message,
    };
  } finally {
    dangTaiPanel.value = false;
  }
};

const moHoiThoai = id =>
  router.push(frontendURL(`accounts/${accountId.value}/conversations/${id}`));

const xuatCsv = () => {
  const csv = dungCsv(COT_CSV_THEO_TAB[tab.value], hangs.value, nhan);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' })
  );
  a.download = `crm-${tab.value}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const xoaLoc = () => {
  boLoc.value = { from: '', to: '', inbox_id: '' };
  nap();
};

const doiCot = khoa => {
  const bo = cotBat.value[tab.value];
  if (bo.has(khoa)) bo.delete(khoa);
  else bo.add(khoa);
};

// Panel hoi thoai doc thang tu row - khong goi them API.
const dongPanelHoiThoai = hang => {
  const i = hang?.inbox || {};
  return [
    { nhan: 'HOI_THOAI', giatri: i.conversation_id },
    { nhan: 'TRANG', giatri: i.inbox_name },
    { nhan: 'TRANG_THAI', giatri: i.status },
    { nhan: 'PHU_TRACH', giatri: i.assignee },
    { nhan: 'AGENT_REP', giatri: (i.agents || []).join(', ') },
    { nhan: 'SO_TIN', giatri: i.message_count },
    { nhan: 'SO_HOI_THOAI', giatri: i.conversation_count },
  ];
};

const theCuaHoiThoai = hang => hang?.inbox?.labels || [];

const oTrong = gt => gt === null || gt === undefined || gt === '';

watch(chiTuQuangCao, nap);
onMounted(() => {
  if (TAB.includes(route.query.tab)) tab.value = route.query.tab;
  nap();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Khong co <h1>: muc menu ben trai da noi day la trang CRM roi. Tab va
         nhom nut nam CUNG mot hang - chung deu la thanh dieu khien cua bang. -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-6 pt-5">
      <!-- Tab kieu khoi phan doan: ranh chim, tab dang chon noi len. Doc ra ngay
           la ba lua chon loai tru nhau chu khong phai ba lien ket. -->
      <div
        class="inline-flex gap-0.5 p-0.5 border rounded-xl border-n-weak bg-n-slate-3"
        role="tablist"
      >
        <button
          v-for="ten in TAB"
          :key="ten"
          role="tab"
          :aria-selected="tab === ten"
          class="flex items-center gap-2 px-3.5 py-1.5 text-sm rounded-lg transition-colors"
          :class="
            tab === ten
              ? 'bg-white dark:bg-n-solid-1 text-n-slate-12 font-medium shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="doiTab(ten)"
        >
          {{ nhan(`TAB_${ten.toUpperCase()}`) }}
          <!-- So dem chi hien o tab dang mo, va chi khi da nap xong: hien "0"
               luc dang tai doc ra thanh "khong co gi", sai han y nghia. -->
          <span
            v-if="tab === ten && !dangTai"
            class="px-1.5 py-px text-xs rounded-full tabular-nums bg-n-brand/15 text-n-brand"
          >
            {{ tong }}
          </span>
        </button>
      </div>

      <div class="flex items-center gap-2">
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :label="nhan('CHON_COT')"
          @click="hienBangChonCot = !hienBangChonCot"
        />
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :label="nhan('XUAT_CSV')"
          :disabled="!hangs.length"
          @click="xuatCsv"
        />
        <Button
          size="sm"
          variant="faded"
          color="slate"
          :label="nhan('TAI_LAI')"
          :is-loading="dangTai"
          @click="nap"
        />
      </div>
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- Nen XAM de the trang noi len. Khong dung n-solid-*: o che do sang
           Chatwoot dat solid-1 = solid-2 = solid-3 = trang tinh, khong co bac
           nao ca (xem _next-colors.scss). n-slate-* moi co bac that o CA HAI
           che do sang/toi. -->
      <div
        class="flex flex-col flex-1 min-w-0 min-h-0 gap-3 px-6 py-4 bg-n-slate-2"
      >
        <!-- `reset-base` tren input va `mb-0` tren select la BAT BUOC: Chatwoot
             ap `field-base` (co mb-4) len moi input[type] va select trong
             _base.scss. 16px le duoi do day o nhap len, con o tick la <label>
             nen khong dinh - do la cho lech nhau.
             Selector cua input co san cua thoat `:not(.reset-base)`; select thi
             khong, phai de len bang class - va phai de CA `py-0`, vi field-base
             cung ap `py-2`: 32px chieu cao tru 16px dem con 16px cho dong chu
             cao 20px, duoi chu `g` bi cat mat.

             Thanh loc kieu khoi vien: nhan nam tren o nhap, tat ca can theo
             DAY. O tick duoc boc trong khung cao bang o nhap, khong thi no tut
             xuong mot duong chan khac. Nut xoa day sang phai vi no la hanh dong,
             khong phai mot bo loc nua. -->
        <div
          v-if="tab !== 'ad'"
          class="flex flex-wrap items-end gap-3.5 px-4 py-3.5 bg-white border shadow-sm rounded-xl border-n-weak dark:bg-n-solid-1"
        >
          <label
            class="flex items-center h-8 gap-2 px-3 text-sm transition-colors border rounded-lg cursor-pointer select-none"
            :class="
              chiTuQuangCao
                ? 'bg-n-brand/10 border-transparent text-n-brand font-medium'
                : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-n-slate-6 hover:text-n-slate-12'
            "
          >
            <input
              v-model="chiTuQuangCao"
              type="checkbox"
              class="cursor-pointer accent-n-brand"
            />
            {{ nhan('CHI_TU_QC') }}
          </label>

          <template v-if="tab === 'khach'">
            <div class="flex flex-col gap-1.5">
              <span
                class="text-[11px] font-medium tracking-wider uppercase text-n-slate-10"
              >
                {{ nhan('LOC_TU') }}
              </span>
              <input
                v-model="boLoc.from"
                type="date"
                class="h-8 px-2.5 text-sm transition-colors border rounded-lg reset-base border-n-weak bg-n-solid-1 text-n-slate-12 hover:border-n-slate-6"
                @change="nap"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <span
                class="text-[11px] font-medium tracking-wider uppercase text-n-slate-10"
              >
                {{ nhan('LOC_DEN') }}
              </span>
              <input
                v-model="boLoc.to"
                type="date"
                class="h-8 px-2.5 text-sm transition-colors border rounded-lg reset-base border-n-weak bg-n-solid-1 text-n-slate-12 hover:border-n-slate-6"
                @change="nap"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <span
                class="text-[11px] font-medium tracking-wider uppercase text-n-slate-10"
              >
                {{ nhan('LOC_TRANG') }}
              </span>
              <select
                v-model="boLoc.inbox_id"
                class="h-8 py-0 pl-2.5 pr-7 mb-0 text-sm transition-colors border rounded-lg cursor-pointer border-n-weak bg-n-solid-1 text-n-slate-12 hover:border-n-slate-6"
                @change="nap"
              >
                <option value="">{{ nhan('MOI_TRANG') }}</option>
                <option v-for="i in inboxes" :key="i.id" :value="i.id">
                  {{ i.name }}
                </option>
              </select>
            </div>
            <div class="flex items-center">
              <Button
                size="sm"
                variant="ghost"
                color="slate"
                :label="nhan('XOA_LOC')"
                @click="xoaLoc"
              />
            </div>
          </template>
        </div>

        <div
          v-if="hienBangChonCot"
          class="p-3 bg-white border shadow-sm rounded-xl border-n-weak dark:bg-n-solid-1"
        >
          <div class="flex flex-wrap gap-x-4 gap-y-2">
            <label
              v-for="c in cotTab"
              :key="c.khoa"
              class="flex items-center gap-1.5 text-sm cursor-pointer text-n-slate-11"
            >
              <input
                type="checkbox"
                :checked="cotBat[tab].has(c.khoa)"
                class="cursor-pointer"
                @change="doiCot(c.khoa)"
              />
              {{ nhan(c.nhan) }}
            </label>
          </div>
        </div>

        <p v-if="loi" class="text-sm text-n-ruby-11">{{ loi }}</p>

        <!-- Chinh khung nay cuon (khong phai ca trang) thi `sticky` moi bam
             duoc. `shadow` thay cho `border-b`: o che do border-collapse, vien
             cua o bi bo lai phia sau khi header dinh len. -->
        <div
          class="flex-1 min-h-0 overflow-auto bg-white border shadow-sm rounded-xl border-n-weak dark:bg-n-solid-1"
        >
          <!-- Luoi du lieu: co vach COT, khong co duong ke NGANG - hang phan
               biet nhau bang nen xen ke. Hang sat nhau de mot man chua nhieu
               dong nhat.
               `odd:` phai dung truoc `hover:` trong thu tu variant cua Tailwind,
               nen dong dang re chuot van de len duoc nen soc. -->
          <table class="w-full text-[13px] border-collapse font-inter">
            <thead class="sticky top-0 z-10">
              <tr
                class="bg-n-slate-3 shadow-[0_1px_0_0_rgb(var(--border-weak))]"
              >
                <th
                  v-for="c in cotHien"
                  :key="c.khoa"
                  class="px-3 py-2 text-[11px] font-medium tracking-wider text-left uppercase border-r whitespace-nowrap text-n-slate-10 border-n-slate-4 last:border-r-0"
                >
                  {{ nhan(c.nhan) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(h, i) in hangs"
                :key="h.id ?? i"
                class="transition-colors hover:bg-n-brand/10"
              >
                <td
                  v-for="c in cotHien"
                  :key="c.khoa"
                  :title="(c.layCsv || c.lay)(h) || ''"
                  class="px-3 py-1.5 align-top border-r text-n-slate-12 border-n-slate-4 last:border-r-0 first:font-medium"
                  :class="[
                    c.dai ? 'max-w-md whitespace-normal' : 'whitespace-nowrap',
                    c.kieu === 'id' ? 'font-mono text-xs tabular-nums' : '',
                    c.kieu === 'so' ? 'tabular-nums text-n-slate-11' : '',
                  ]"
                >
                  <button
                    v-if="c.khoaLink && c.lay(h)"
                    class="underline text-n-brand underline-offset-2 decoration-dotted"
                    @click="moPanel(c, h)"
                  >
                    {{ c.lay(h) }}
                  </button>
                  <!-- 4/9 cot dang rong hoan toan; dau gach de dam thi ca
                       bang thanh mot vung nhieu xam. -->
                  <span v-else-if="oTrong(c.lay(h))" class="text-n-slate-6">
                    —
                  </span>
                  <span v-else>{{ c.lay(h) }}</span>
                </td>
              </tr>
              <tr v-if="!hangs.length && !dangTai">
                <td
                  :colspan="cotHien.length"
                  class="px-3 py-8 text-sm text-center text-n-slate-10"
                >
                  {{ nhan('TRONG') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- BonBoZ: duoi 768px panel thanh LOP PHU toan man thay vi cot ben canh.
           Cot 384px cong voi bang thi tren dien thoai no day bang ra ngoai va
           chiem gan het be ngang, con lai mot dai bang khong doc duoc gi. -->
      <aside
        v-if="panel"
        class="flex fixed inset-0 z-40 flex-col gap-3 px-4 py-4 border-l md:static md:z-auto md:w-96 overflow-auto border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center justify-between">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ nhan(panel.loai === 'ad' ? 'PANEL_AD' : 'PANEL_HOI_THOAI') }}
          </h2>
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            @click="panel = null"
          />
        </div>

        <p v-if="dangTaiPanel" class="text-sm text-n-slate-10">
          {{ nhan('DANG_TAI') }}
        </p>
        <p v-else-if="panel.loi" class="text-sm text-n-ruby-11">
          {{ nhan('LOI') }}: {{ panel.loi }}
        </p>

        <template v-else-if="panel.loai === 'ad' && panel.du_lieu">
          <img
            v-if="panel.du_lieu.creative_image_url"
            :src="panel.du_lieu.creative_image_url"
            :alt="panel.du_lieu.campaign_name"
            class="w-full rounded-lg"
          />
          <dl class="flex flex-col gap-2 text-sm">
            <div v-for="k in TRUONG_PANEL_AD" :key="k">
              <dt class="text-xs text-n-slate-11">{{ k }}</dt>
              <dd class="break-words text-n-slate-12">
                {{ oTrong(panel.du_lieu[k]) ? '—' : panel.du_lieu[k] }}
              </dd>
            </div>
          </dl>
          <p
            v-if="panel.du_lieu.post_message"
            class="p-2 text-sm rounded-lg text-n-slate-11 bg-n-alpha-1 whitespace-pre-wrap"
          >
            {{ panel.du_lieu.post_message }}
          </p>
          <a
            v-if="panel.du_lieu.permalink_url"
            :href="panel.du_lieu.permalink_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm underline text-n-brand"
          >
            {{ nhan('XEM_QUANG_CAO') }}
          </a>
          <div v-if="panel.du_lieu.khach?.length" class="flex flex-col gap-1">
            <span class="text-xs text-n-slate-11">
              {{ nhan('PANEL_KHACH_CUA_AD') }}
            </span>
            <button
              v-for="k in panel.du_lieu.khach"
              :key="`${k.contact_id}-${k.occurred_at}`"
              class="flex items-center justify-between px-2 py-1 text-sm text-left rounded hover:bg-n-alpha-1"
              @click="k.conversation_id && moHoiThoai(k.conversation_id)"
            >
              <span class="text-n-slate-12">{{ k.contact_name || '—' }}</span>
              <span class="text-xs text-n-slate-10">
                #{{ k.conversation_id }}
              </span>
            </button>
          </div>
        </template>

        <template v-else-if="panel.loai === 'inbox'">
          <dl class="flex flex-col gap-2 text-sm">
            <div v-for="d in dongPanelHoiThoai(panel.hang)" :key="d.nhan">
              <dt class="text-xs text-n-slate-11">{{ nhan(d.nhan) }}</dt>
              <dd class="break-words text-n-slate-12">
                {{ oTrong(d.giatri) ? '—' : d.giatri }}
              </dd>
            </div>
          </dl>
          <div
            v-if="theCuaHoiThoai(panel.hang).length"
            class="flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-11">{{ nhan('THE') }}</span>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="the in theCuaHoiThoai(panel.hang)"
                :key="the"
                class="px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-11"
              >
                {{ the }}
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="nhan('MO_HOI_THOAI')"
            @click="moHoiThoai(panel.conversation_id)"
          />
        </template>
      </aside>
    </div>
  </div>
</template>
